import { useMemo, useState } from "react";
import { TrendingUp, Calendar, Euro, BarChart3, Zap, Package } from "lucide-react";

/**
 * Compound ROI projection for Blog Packs.
 *
 * Model (conservative industry averages):
 * - Average SEO article reaches ~20 monthly organic visits after a 3-month ramp-up.
 * - Organic traffic compounds: new articles add to the cumulative base each month.
 * - Avg CPC €2 (editable) represents the ads spend saved for equivalent traffic.
 * - Conversion: 1.5% of traffic converts; avg order value €50.
 *
 * A/B toggle (`mode`):
 * - "subscribe" → +N articles every month (compounding)
 * - "one_time"  → N articles once, month 1; no new articles afterwards (flat line)
 *
 * Purpose: demonstrate compounding value of staying subscribed — improves retention.
 */

const VISITS_PER_ARTICLE = 20;
const RAMP_UP_MONTHS = 3;
const DEFAULT_CPC = 2.0;
const CONV_RATE = 0.015;
const AOV = 50;

function projectMonth(articlesPerMonth, monthN, mode) {
  let visits = 0;
  for (let m = 1; m <= monthN; m++) {
    // In one_time mode, only month 1 adds articles; after that it's a dead stock.
    const articlesThisMonth = mode === "one_time" && m > 1 ? 0 : articlesPerMonth;
    const age = monthN - m + 1;
    const maturity = Math.min(1, age / RAMP_UP_MONTHS);
    visits += articlesThisMonth * VISITS_PER_ARTICLE * maturity;
  }
  return Math.round(visits);
}

export default function CompoundROIWidget({ packs = [] }) {
  // Derive user's effective articles/month from their packs
  const articlesPerMonth = useMemo(() => {
    let total = 0;
    packs.forEach((p) => {
      if (p.is_subscription && p.status === "active") {
        total += p.pack_size || 0;
      } else if (p.status === "active") {
        total += (p.pack_size || 0);
      }
    });
    return total || 100;
  }, [packs]);

  const [cpc, setCpc] = useState(DEFAULT_CPC);
  const [mode, setMode] = useState("subscribe"); // "subscribe" | "one_time"

  const months = [1, 3, 6, 12];
  const projections = months.map((m) => {
    const traffic = projectMonth(articlesPerMonth, m, mode);
    const adValueSaved = Math.round(traffic * cpc);
    const revenue = Math.round(traffic * CONV_RATE * AOV);
    return { month: m, traffic, adValueSaved, revenue };
  });

  // Compare: subscribe vs one_time at month 12 to show the gap
  const sub12 = projectMonth(articlesPerMonth, 12, "subscribe");
  const once12 = projectMonth(articlesPerMonth, 12, "one_time");
  const gapPct = sub12 > 0 ? Math.round(((sub12 - once12) / sub12) * 100) : 0;

  const lifetime12 = projections.find((p) => p.month === 12);
  const cost12 = mode === "subscribe" ? 49 * 12 : 50; // €588 sub or €50 one-time
  const costLabel = mode === "subscribe" ? `€${cost12} sub` : `€${cost12} one-time`;
  const roi = lifetime12 && cost12 > 0 ? Math.round((lifetime12.adValueSaved / cost12 - 1) * 100) : 0;

  return (
    <div
      data-testid="compound-roi-widget"
      className="bg-gradient-to-br from-indigo-50 via-white to-emerald-50 border border-indigo-200 rounded-2xl p-5"
    >
      <div className="flex items-start justify-between gap-4 mb-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-4 h-4 text-indigo-600" />
            <p className="text-[10px] font-mono uppercase tracking-widest font-bold text-indigo-600">
              Compound ROI Projection
            </p>
          </div>
          <p className="text-sm text-gray-600">
            Based on <span className="font-bold text-gray-900">{articlesPerMonth} articles/month</span>.
            Every article keeps earning traffic long after publication.
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-gray-600">
          Avg CPC:
          <div className="flex items-center bg-white border border-gray-300 rounded-lg px-2 py-1 gap-1">
            <Euro className="w-3 h-3 text-gray-500" />
            <input
              type="number"
              min="0.1"
              max="50"
              step="0.1"
              value={cpc}
              onChange={(e) => setCpc(Math.max(0.1, parseFloat(e.target.value) || 0.1))}
              data-testid="roi-cpc-input"
              className="w-14 text-sm font-bold focus:outline-none"
            />
          </div>
        </label>
      </div>

      {/* A/B toggle: Subscribe vs Pay Once */}
      <div
        data-testid="roi-mode-toggle"
        className="flex items-center p-1 bg-white border border-gray-200 rounded-full w-fit mx-auto mb-4 shadow-sm"
      >
        <button
          type="button"
          onClick={() => setMode("subscribe")}
          data-testid="roi-mode-subscribe"
          className={`px-4 py-1.5 text-xs font-bold rounded-full flex items-center gap-1.5 transition-all ${
            mode === "subscribe"
              ? "bg-indigo-600 text-white shadow"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <Zap className="w-3 h-3" />
          Subscribe €49/mo
        </button>
        <button
          type="button"
          onClick={() => setMode("one_time")}
          data-testid="roi-mode-once"
          className={`px-4 py-1.5 text-xs font-bold rounded-full flex items-center gap-1.5 transition-all ${
            mode === "one_time"
              ? "bg-gray-900 text-white shadow"
              : "text-gray-600 hover:text-gray-900"
          }`}
        >
          <Package className="w-3 h-3" />
          Pay once €50
        </button>
      </div>

      {/* Gap callout — only shown in one_time mode */}
      {mode === "one_time" && (
        <div
          data-testid="roi-gap-callout"
          className="mb-4 px-4 py-2 rounded-lg bg-amber-50 border border-amber-200 text-[12px] text-amber-900 text-center"
        >
          ⚠️ After month 1 your traffic plateaus. Subscription keeps compounding —
          <span className="font-black"> {gapPct}% more traffic at month 12</span>.
        </div>
      )}

      {/* Projection grid */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
        {projections.map((p) => (
          <div
            key={p.month}
            data-testid={`roi-month-${p.month}`}
            className="bg-white rounded-xl p-3 border border-gray-100 shadow-sm"
          >
            <div className="flex items-center gap-1.5 mb-2 text-gray-500">
              <Calendar className="w-3 h-3" />
              <span className="text-[10px] font-mono uppercase tracking-widest font-bold">
                Month {p.month}
              </span>
            </div>
            <p className="text-2xl font-black text-gray-900 leading-none">
              {p.traffic.toLocaleString()}
            </p>
            <p className="text-[10px] text-gray-500 mt-0.5">organic visits/mo</p>
            <div className="mt-2 pt-2 border-t border-gray-100 space-y-0.5">
              <p className="text-[11px] text-emerald-700 font-bold">
                €{p.adValueSaved.toLocaleString()} saved ads
              </p>
              <p className="text-[11px] text-indigo-700 font-bold">
                €{p.revenue.toLocaleString()} revenue
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Hero ROI number */}
      {lifetime12 && articlesPerMonth > 0 && (
        <div
          data-testid="roi-12mo-hero"
          className="bg-gradient-to-r from-indigo-600 to-emerald-600 text-white rounded-xl p-4 flex items-center justify-between gap-4 flex-wrap"
        >
          <div>
            <p className="text-[10px] font-mono uppercase tracking-widest opacity-80 mb-1">
              12-month outlook
            </p>
            <p className="text-xl sm:text-2xl font-black leading-tight">
              €{lifetime12.adValueSaved.toLocaleString()} in ad spend saved
            </p>
            <p className="text-xs opacity-90">
              + €{lifetime12.revenue.toLocaleString()} projected revenue from {lifetime12.traffic.toLocaleString()} monthly visits
            </p>
          </div>
          <div className="text-right">
            <p className="text-[10px] font-mono uppercase tracking-widest opacity-80 mb-1">
              ROI vs {costLabel}
            </p>
            <p className="text-3xl sm:text-4xl font-black leading-none">
              {roi > 0 ? "+" : ""}
              {roi.toLocaleString()}%
            </p>
          </div>
        </div>
      )}

      <p className="text-[10px] text-gray-500 mt-3 flex items-center gap-1.5">
        <BarChart3 className="w-3 h-3" />
        Based on industry averages: 20 visits/article steady-state, 3-month ramp-up, 1.5% conversion rate, €50 AOV.
      </p>
    </div>
  );
}
